import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { acceptInvitation, checkInvitation } from "@/api/endpoints";
import { formatDate } from "@/lib/format";
import type { InvitationCheck } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

type Stage = "loading" | "ready" | "submitting" | "invalid";

export function InvitationAccept(): JSX.Element {
  usePageTitle("Accept invitation");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [stage, setStage] = useState<Stage>("loading");
  const [check, setCheck] = useState<InvitationCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    if (!token) {
      setStage("invalid");
      setError("Missing token. Use the link from your invitation email.");
      return;
    }
    checkInvitation(token)
      .then((c) => {
        setCheck(c);
        setStage("ready");
      })
      .catch((err: unknown) => {
        const msg = err instanceof ApiError ? err.message : "Invalid invitation";
        setError(msg);
        setStage("invalid");
      });
  }, [token]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setStage("submitting");
    setError(null);
    try {
      await acceptInvitation(token, password);
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Could not accept invitation";
      setError(msg);
      setStage("ready");
    }
  }

  return (
    <main className="min-h-full flex items-center justify-center px-4">
      <div className="card w-full max-w-md space-y-4">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold">AquaTrack</h1>
          <p className="text-sm text-ink-300">Accept your invitation</p>
        </header>

        {stage === "loading" && <div className="skeleton h-24" aria-label="Validating token" />}

        {stage === "invalid" && (
          <p className="text-sm text-severity-high" role="alert">
            {error ?? "This invitation is no longer valid."}
          </p>
        )}

        {(stage === "ready" || stage === "submitting") && check && (
          <>
            <section className="text-sm space-y-1">
              <p>Email: <strong>{check.email}</strong></p>
              <p>Role: <strong>{check.scope} / {check.role}</strong></p>
              {check.tenant_name && <p>Tenant: <strong>{check.tenant_name}</strong></p>}
              <p className="text-ink-400">Expires {formatDate(check.expires_at)}</p>
            </section>
            <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
              <label className="block space-y-1">
                <span className="text-sm text-ink-200">Set password (min 12 chars)</span>
                <input
                  type="password"
                  required
                  minLength={12}
                  autoComplete="new-password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <label className="block space-y-1">
                <span className="text-sm text-ink-200">Confirm password</span>
                <input
                  type="password"
                  required
                  minLength={12}
                  autoComplete="new-password"
                  className="input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </label>
              {error && (
                <p className="text-sm text-severity-high" role="alert">{error}</p>
              )}
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={stage === "submitting"}
              >
                {stage === "submitting" ? "Setting up…" : "Accept and sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
